import { t as tr } from "@/lib/i18n";
import { useState } from "react";
import {
  Activity,
  ArrowUpRight,
  Bot,
  Cloud,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";

import { Badge, Button, cn } from "@fox1ck-org/ui";
import { RoleGate } from "@/components/auth/role-gate";
import {
  statusLabel,
  useDeleteIntegration,
  useTestIntegration,
  type ProviderConnections,
  type ProviderMeta,
} from "@/lib/services/integration-service";
import { Integration, IntegrationStatus, IntegrationProvider } from "@gen/tracker/v1/integration_pb";
import { formatDate } from "@/lib/utils";

export type ConnectionScope = "workspace" | "platform";

interface ProviderCardProps {
  meta: ProviderMeta;
  connections: ProviderConnections;
  isPlatformAdmin: boolean;
  onConnect: (scope: ConnectionScope) => void;
  onEdit: (scope: ConnectionScope) => void;
}

const STATUS_DOT: Record<IntegrationStatus, string> = {
  [IntegrationStatus.UNSPECIFIED]: "bg-muted-foreground/40",
  [IntegrationStatus.HEALTHY]: "bg-ok",
  [IntegrationStatus.DEGRADED]: "bg-warn",
  [IntegrationStatus.FAILING]: "bg-destructive",
  [IntegrationStatus.DISABLED]: "bg-muted-foreground/40",
};

// Per-provider icon tile. Subtle brand tints keep the catalog scannable
// without leaving the app's token system.
const PROVIDER_TILE: Partial<
  Record<IntegrationProvider, { icon: typeof Cloud; tint: string }>
> = {
  [IntegrationProvider.CLOUDFLARE]: {
    icon: Cloud,
    tint: "bg-[oklch(0.72_0.15_60_/_0.14)] text-[oklch(0.62_0.16_55)]",
  },
  [IntegrationProvider.OPENROUTER]: {
    icon: Bot,
    tint: "bg-[oklch(0.6_0.14_252_/_0.12)] text-primary",
  },
};

// ProviderCard renders one provider's connection state:
//   1. workspace connection  → status + diagnostics + Test/Edit/Remove
//   2. platform default only → "Using platform default" + Override CTA
//   3. nothing               → muted card + Connect CTA
// Platform admins additionally see + manage the platform-default row inline.
export function ProviderCard({
  meta,
  connections,
  isPlatformAdmin,
  onConnect,
  onEdit,
}: ProviderCardProps) {
  const { workspace, platform } = connections;
  const tile = PROVIDER_TILE[meta.provider]!;
  const Icon = tile.icon;
  const configured = !!workspace || !!platform;

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-3 rounded-2xl border bg-card p-4 transition-colors",
        configured ? "border-border" : "border-dashed border-border/80",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            tile.tint,
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{meta.label}</h3>
            {workspace ? (
              <Badge variant="primary" className="text-xs">{tr("tpl.scope.ws")}</Badge>
            ) : platform ? (
              <Badge variant="neutral" className="text-xs">{tr("conn.platformDefault")}</Badge>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {meta.tagline}
          </p>
        </div>
      </div>

      {workspace ? (
        <ConnectionRow
          row={workspace}
          label={workspace.name || tr("conn.wsConnection")}
          onEdit={() => onEdit("workspace")}
          removable={{ fallback: platform ? tr("conn.fallback") : undefined }}
        />
      ) : platform ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", STATUS_DOT[platform.status])} />
            <span className="truncate">
              {tr("conn.usingShared", { provider: meta.label })}
            </span>
          </div>
          <RoleGate need="manage" label={tr("conn.override")}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 gap-1 px-2 text-xs"
              onClick={() => onConnect("workspace")}
            >
              {tr("conn.override")} <ArrowUpRight className="h-3 w-3" />
            </Button>
          </RoleGate>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2">
          <span className="text-xs text-muted-foreground">{tr("conn.notConfigured")}</span>
          <RoleGate need="manage" label={tr("conn.connect")}>
            <Button
              variant="primary"
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs"
              onClick={() => onConnect("workspace")}
            >
              <Plus className="h-3 w-3" /> {tr("conn.connect")}
            </Button>
          </RoleGate>
        </div>
      )}

      {isPlatformAdmin ? (
        <div className="border-t border-border/60 pt-2.5">
          {platform ? (
            <ConnectionRow
              row={platform}
              label={tr("conn.platformDefault")}
              subtle
              onEdit={() => onEdit("platform")}
              removable={{}}
            />
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground/80">
                {tr("conn.noDefault")}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 shrink-0 px-2 text-xs"
                onClick={() => onConnect("platform")}
              >
                <Plus className="mr-1 h-3 w-3" /> {tr("conn.setDefault")}
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ConnectionRow shows one stored connection (workspace or platform) with its
// health + Test / Edit / Remove actions.
function ConnectionRow({
  row,
  label,
  subtle,
  onEdit,
  removable,
}: {
  row: Integration;
  label: string;
  subtle?: boolean;
  onEdit: () => void;
  removable: { fallback?: string };
}) {
  const test = useTestIntegration();
  const remove = useDeleteIntegration();
  const [confirming, setConfirming] = useState(false);

  async function handleTest() {
    try {
      const res = await test.mutateAsync(row.id);
      if (res.ok) toast.success(tr("conn.ok", { label }));
      else toast.error(`${label}: ${res.message || tr("conn.probeFailed")}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("conn.probeFailed"));
    }
  }

  async function handleRemove() {
    if (!confirming) {
      setConfirming(true);
      window.setTimeout(() => setConfirming(false), 3000);
      return;
    }
    try {
      await remove.mutateAsync(row.id);
      toast.success(
        removable.fallback
          ? tr("conn.removedFallback", { fallback: removable.fallback })
          : tr("conn.removed"),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("rule.ed.deleteFailed"));
    }
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-lg px-3 py-2",
        subtle ? "bg-transparent" : "border border-border bg-muted/30",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn("inline-block h-2 w-2 shrink-0 rounded-full", STATUS_DOT[row.status])}
          title={statusLabel(row.status)}
        />
        <div className="min-w-0">
          <div className="truncate text-xs font-medium text-foreground">{label}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.lastError ? (
              <span className="text-destructive" title={row.lastError}>
                {row.lastError}
              </span>
            ) : row.lastSyncedAt ? (
              <>{tr("conn.checked", { when: formatDate(row.lastSyncedAt, "d MMM, HH:mm") })}</>
            ) : (
              tr("conn.neverTested")
            )}
          </div>
        </div>
      </div>
      <RoleGate need="manage" label={tr("conn.editCreds")}>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={handleTest}
            disabled={test.isPending}
            title={tr("conn.probeStored")}
          >
            <Activity className={cn("h-3.5 w-3.5", test.isPending && "animate-pulse")} />
            {tr("conn.testShort")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 px-0"
            onClick={onEdit}
            title={tr("conn.editCreds")}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-7 px-2 text-xs", confirming && "text-destructive")}
            onClick={handleRemove}
            disabled={remove.isPending}
            title={tr("conn.remove")}
          >
            {confirming ? tr("conn.sure") : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </RoleGate>
    </div>
  );
}
